'use client';

interface MetricGaugeProps {
  value: number;
  max: number;
  label: string;
  unit?: string;
  ranges?: Array<{
    min: number;
    max: number;
    color: string;
    label: string;
  }>;
}

const BRAND_COLORS = {
  navy: '#0A1E2E',
  orange: '#E89841',
  cream: '#E8D5B7',
  green: '#6FCF97',
  blue: '#4A90E2',
  yellow: '#F2C94C',
  red: '#EB5757',
};

const DEFAULT_RANGES = [
  { min: 0, max: 25, color: BRAND_COLORS.red, label: 'Poor' },
  { min: 25, max: 50, color: BRAND_COLORS.yellow, label: 'Fair' },
  { min: 50, max: 75, color: BRAND_COLORS.blue, label: 'Good' },
  { min: 75, max: 100, color: BRAND_COLORS.green, label: 'Excellent' },
];

export default function MetricGauge({
  value,
  max,
  label,
  unit = '',
  ranges = DEFAULT_RANGES,
}: MetricGaugeProps) {
  const percentage = (value / max) * 100;

  // Determine current range
  const currentRange = ranges.find(r => percentage >= r.min && percentage <= r.max) || ranges[0];

  // Calculate segments for visual display
  const segments = ranges.map(range => ({
    ...range,
    width: ((range.max - range.min) / 100) * 100,
  }));

  return (
    <div className="w-full my-6 p-6 bg-background/40 border border-border rounded-lg">
      <div className="max-w-md mx-auto">
        {/* Label */}
        <h3 className="text-lg font-semibold text-foreground text-center mb-4">{label}</h3>

        {/* Gauge Bar */}
        <div className="relative h-8 bg-background/60 rounded-full overflow-hidden mb-4">
          {/* Colored segments (background) */}
          <div className="absolute inset-0 flex">
            {segments.map((segment, index) => (
              <div
                key={index}
                className="h-full opacity-30"
                style={{
                  width: `${segment.width}%`,
                  backgroundColor: segment.color,
                }}
              />
            ))}
          </div>

          {/* Progress fill */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: currentRange.color,
            }}
          />
        </div>

        {/* Value Display */}
        <div className="text-center">
          <div className="text-3xl font-bold" style={{ color: currentRange.color }}>
            {value.toLocaleString()}{unit}
          </div>
          <div className="text-sm text-foreground-secondary mt-1">
            {currentRange.label} ({percentage.toFixed(1)}%)
          </div>
        </div>

        {/* Legend */}
        <div className="flex justify-between mt-4 text-xs text-foreground-tertiary">
          <span>0{unit}</span>
          <span>{max.toLocaleString()}{unit}</span>
        </div>
      </div>
    </div>
  );
}
